// ---------------------------------------------------------------
// swapService.js — Intercambio de turnos entre compañeros.
//
// Colección `swapRequests`:
//   { fromUid, fromName, fromDate,   ← quien propone y su día
//     toUid, toName, toDate,         ← compañero y su día
//     status: "pending_peer"    (esperando al compañero)
//           | "pending_admin"   (compañero aceptó; falta el admin)
//           | "rejected_peer" | "rejected_admin" | "approved",
//     createdAt, updatedAt }
//
// Al aprobar, los dos días se INTERCAMBIAN automáticamente en los
// horarios de ambos y los totales se recalculan.
// ---------------------------------------------------------------
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { getMonthSchedulesOnce } from "./scheduleService";
import { savePresenceForUser } from "./presenceService";

export function createSwapRequest({ fromUid, fromName, fromDate, toUid, toName, toDate }) {
  return addDoc(collection(db, "swapRequests"), {
    fromUid,
    fromName: fromName || "",
    fromDate,
    toUid,
    toName: toName || "",
    toDate,
    status: "pending_peer",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** El compañero acepta (pasa al admin) o rechaza */
export function peerRespond(requestId, accept) {
  return updateDoc(doc(db, "swapRequests", requestId), {
    status: accept ? "pending_admin" : "rejected_peer",
    updatedAt: serverTimestamp(),
  });
}

/* ================================================================
   COMPROBACIONES ANTES DE APROBAR

   Un intercambio es la única puerta por la que se puede modificar el
   cuadrante sin pasar por el generador, y hasta ahora entraba a
   ciegas: cambiaba los dos días y ya. El cuadrante, sin embargo, es
   un conjunto con reglas (ver scheduleGenerator.js), y saltárselas
   aquí puede dejar la farmacia sin farmacéutico de tarde, que no es
   una preferencia de organización sino una obligación.

   Esto NO impide nada: devuelve la lista de lo que se rompería para
   que el administrador lo vea y decida. Igual que el guardado
   selectivo del horario.
   ================================================================ */

/** ¿El turno empieza a las 15:00 o después? */
const esDeTarde = (t) => Number(String(t.start).slice(0, 2)) >= 15;

/**
 * Qué se rompe si se aprueba este intercambio.
 * @returns lista de avisos en texto (vacía = todo correcto)
 */
export async function avisosDelIntercambio(req, { roles, availability } = {}) {
  const avisos = [];
  try {
    const { fromUid, toUid, fromDate, toDate } = req;
    const farmaceuticos = new Set(
      [roles?.morningPharmacistUid, ...(roles?.pharmacistPair || [])].filter(
        Boolean
      )
    );

    // Los dos días que cambian de manos, cada uno con su nuevo dueño
    const tramos = [
      { iso: fromDate, cede: fromUid, recibe: toUid },
      { iso: toDate, cede: toUid, recibe: fromUid },
    ];

    for (const { iso, cede, recibe } of tramos) {
      const mes = iso.slice(0, 7);
      const dia = String(Number(iso.slice(8, 10)));
      const delMes = await getMonthSchedulesOnce(mes);
      const porUid = {};
      for (const d of delMes) if (d?.userId) porUid[d.userId] = d.days || {};

      const turnoQueCambia = porUid[cede]?.[dia]?.shifts || [];
      if (!turnoQueCambia.length) continue; // día libre: no rompe nada

      // ¿Quien lo recibe puede ese día?
      const av = availability || {};
      if (av.availableFrom?.[recibe] && iso < av.availableFrom[recibe]) {
        avisos.push(`El ${iso} esa persona aún no se había incorporado.`);
      }
      const bajaDesde = av.unavailableFrom?.[recibe];
      const bajaHasta = av.unavailableUntil?.[recibe];
      if (bajaDesde && iso >= bajaDesde && (!bajaHasta || iso <= bajaHasta)) {
        avisos.push(`El ${iso} quien recibe el turno está de baja.`);
      }
      if (porUid[recibe]?.[dia]?.ownVacation) {
        avisos.push(`El ${iso} quien recibe el turno está de vacaciones.`);
      }
      // ¿Ya tenía turno ese día? Acabaría haciendo dos.
      if ((porUid[recibe]?.[dia]?.shifts || []).length) {
        avisos.push(
          `El ${iso} quien recibe el turno YA tenía otro: acabaría haciendo dos.`
        );
      }

      // La comprobación que de verdad importa: ¿queda farmacéutico de
      // tarde? Sólo se mira si el turno que cambia de manos es de
      // tarde y quien lo cede es farmacéutico.
      if (farmaceuticos.has(cede) && turnoQueCambia.some(esDeTarde)) {
        const otroCubre = [...farmaceuticos].some((uid) => {
          if (uid === cede) return false;
          if (uid === recibe) return true; // el que lo recibe ya vale
          return (porUid[uid]?.[dia]?.shifts || []).some(esDeTarde);
        });
        if (!otroCubre) {
          avisos.push(
            `El ${iso} la farmacia se quedaría SIN FARMACÉUTICO DE TARDE.`
          );
        }
      }

      // Cuánta gente queda en cada franja después del cambio
      const cuenta = (deTarde) => {
        let n = 0;
        for (const [uid, dias] of Object.entries(porUid)) {
          let turnos = dias?.[dia]?.shifts || [];
          if (uid === cede) turnos = []; // lo suelta
          if (uid === recibe) turnos = [...turnos, ...turnoQueCambia];
          if (turnos.some((t) => esDeTarde(t) === deTarde)) n += 1;
        }
        return n;
      };
      const manana = cuenta(false);
      const tarde = cuenta(true);
      if (manana === 1) avisos.push(`El ${iso} quedaría UNA sola persona por la mañana.`);
      if (tarde === 1) avisos.push(`El ${iso} quedaría UNA sola persona por la tarde.`);
      if (manana > 3) avisos.push(`El ${iso} quedarían ${manana} personas por la mañana.`);
      if (tarde > 3) avisos.push(`El ${iso} quedarían ${tarde} personas por la tarde.`);
    }
  } catch (err) {
    // Si no se pueden leer los horarios, se dice: es peor aprobar a
    // ciegas creyendo que se ha comprobado algo.
    console.error("No se pudo comprobar el intercambio:", err);
    return ["No se han podido comprobar los horarios: apruébalo bajo tu criterio."];
  }
  return avisos;
}

/** Intercambia los dos días en los horarios de ambos usuarios */
async function executeSwap(req) {
  const { fromUid, toUid, fromDate, toDate } = req;
  const dayFrom = String(Number(fromDate.slice(8, 10)));
  const dayTo = String(Number(toDate.slice(8, 10)));
  const monthFrom = fromDate.slice(0, 7);
  const monthTo = toDate.slice(0, 7);

  // Cargar los documentos implicados (sin duplicar)
  const keys = new Set([
    `${fromUid}_${monthFrom}`,
    `${fromUid}_${monthTo}`,
    `${toUid}_${monthFrom}`,
    `${toUid}_${monthTo}`,
  ]);
  const daysById = {};
  for (const id of keys) {
    const snap = await getDoc(doc(db, "schedules", id));
    daysById[id] = snap.exists() ? { ...(snap.data().days || {}) } : {};
  }

  const get = (uid, month) => daysById[`${uid}_${month}`];
  const infoA = get(fromUid, monthFrom)[dayFrom] || null; // turno de A su día
  const infoB = get(toUid, monthTo)[dayTo] || null;       // turno de B su día

  // B cubre el día de A…
  if (infoA) get(toUid, monthFrom)[dayFrom] = infoA;
  else delete get(toUid, monthFrom)[dayFrom];
  delete get(fromUid, monthFrom)[dayFrom];

  // …y A cubre el día de B
  if (infoB) get(fromUid, monthTo)[dayTo] = infoB;
  else delete get(fromUid, monthTo)[dayTo];
  delete get(toUid, monthTo)[dayTo];

  /* Guardar TODO DE UNA VEZ.
     Antes eran hasta cuatro `saveSchedule` seguidos: si se cortaba a
     mitad (se va el wifi, se cierra la pestaña) los dos horarios
     quedaban descuadrados —el día podía acabar asignado a los dos, o
     a ninguno— y sin forma de saber que había pasado. Con un lote es
     todo o nada. */
  const lote = writeBatch(db);
  const tocados = [];
  for (const id of keys) {
    const corte = id.lastIndexOf("_");
    const uid = id.slice(0, corte);
    const month = id.slice(corte + 1);
    const days = daysById[id];
    const totalHours =
      Math.round(
        Object.values(days).reduce((sum, d) => sum + (d.hours || 0), 0) * 100
      ) / 100;
    lote.set(doc(db, "schedules", id), {
      userId: uid,
      month,
      days,
      totalHours,
      updatedAt: serverTimestamp(),
    });
    tocados.push({ uid, month, days });
  }
  await lote.commit();

  // La presencia va después y por separado: es un resumen que se
  // puede reconstruir ("Regenerar presencia" en Config. horas), así
  // que no merece bloquear el intercambio si falla.
  for (const { uid, month, days } of tocados) {
    try {
      await savePresenceForUser(month, uid, days);
    } catch (err) {
      console.error("Intercambio hecho, pero la presencia no se actualizó:", err);
    }
  }
}

/** El admin aprueba (intercambia horarios) o rechaza */
export async function adminResolve(request, approve) {
  if (approve) {
    await executeSwap(request);
  }
  await updateDoc(doc(db, "swapRequests", request.id), {
    status: approve ? "approved" : "rejected_admin",
    updatedAt: serverTimestamp(),
  });
}

/** Solicitudes que me afectan (enviadas y recibidas) + pendientes de
 *  admin si soy administrador. */
export function subscribeSwaps(uid, isAdmin, onData, onError) {
  const results = { from: [], to: [], admin: [] };
  const emit = () => {
    const map = {};
    [...results.from, ...results.to, ...results.admin].forEach((r) => (map[r.id] = r));
    const list = Object.values(map);
    list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    onData(list);
  };

  const subs = [
    onSnapshot(
      query(collection(db, "swapRequests"), where("fromUid", "==", uid)),
      (s) => { results.from = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); },
      onError
    ),
    onSnapshot(
      query(collection(db, "swapRequests"), where("toUid", "==", uid)),
      (s) => { results.to = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); },
      onError
    ),
  ];
  if (isAdmin) {
    subs.push(
      onSnapshot(
        query(collection(db, "swapRequests"), where("status", "==", "pending_admin")),
        (s) => { results.admin = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); },
        onError
      )
    );
  }
  return () => subs.forEach((u) => u());
}
